import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { getCompanyDisplayName } from '../utils/companyDisplayName';
import { lastDigits } from '../utils/phoneUtils';
import { formatPersonName } from '../utils/nameUtils';

/**
 * Fase 2 (Contatos C4) — formulário de criação reutilizável.
 *
 * Compartilhado entre Quick Add manual, Import VCF e (futuro)
 * RegisterContactModal do ADR-022 (WhatsApp).
 *
 * Convenções:
 * - Autocomplete inline em company_name/short_name/empresa via ILIKE (300ms debounce)
 * - Dedup por email exato e por últimos 8 dígitos do mobile
 * - Quando dup detectada: 3 ações — vincular (RPC), criar mesmo assim (RPC), cancelar
 * - Telefones armazenados no formato original (D6); não há normalização destrutiva
 * - tipo_contato derivado de empresa.papel_negocio
 *   (buyer→incorporadora, landowner→proprietario, service_provider→consultor,
 *    internal_aston→incorporadora, default→incorporadora)
 */

const PAPEL_TO_TIPO = {
  buyer: 'incorporadora',
  landowner: 'proprietario',
  service_provider: 'consultor',
  internal_aston: 'incorporadora',
};

const deriveTipoContato = (papel) => PAPEL_TO_TIPO[papel] || 'incorporadora';

const ContactCreateForm = ({
  source = 'manual',
  initialValues = {},
  preSelectedEmpresa = null,
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    first_name: initialValues.first_name || '',
    last_name: initialValues.last_name || '',
    email: initialValues.email || '',
    mobile: initialValues.mobile || '',
    phone: initialValues.phone || '',
    title: initialValues.title || '',
  });
  const [empresaSelected, setEmpresaSelected] = useState(preSelectedEmpresa || null);
  const [empresaQuery, setEmpresaQuery] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);
  const [searchingEmpresa, setSearchingEmpresa] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDups, setCheckingDups] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const searchTimerRef = useRef(null);

  const companyDisplay = useMemo(
    () => (empresaSelected ? getCompanyDisplayName(empresaSelected) : ''),
    [empresaSelected],
  );

  // ── Autocomplete empresa (debounce 300ms) ────────────────────────────────
  useEffect(() => {
    if (empresaSelected) return;
    if (!empresaQuery || empresaQuery.trim().length < 2) {
      setEmpresaResults([]);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearchingEmpresa(true);
      const q = empresaQuery.trim();
      const { data, error: err } = await supabase
        .from('empresas')
        .select('id, company_name, short_name, empresa, papel_negocio')
        .or(`company_name.ilike.%${q}%,short_name.ilike.%${q}%,empresa.ilike.%${q}%`)
        .order('company_name')
        .limit(10);
      if (!err) setEmpresaResults(data || []);
      setSearchingEmpresa(false);
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [empresaQuery, empresaSelected]);

  const pickEmpresa = (e) => {
    setEmpresaSelected(e);
    setEmpresaResults([]);
    setEmpresaQuery('');
    setDuplicates([]);
  };
  const clearEmpresa = () => {
    setEmpresaSelected(null);
    setEmpresaQuery('');
  };

  const setField = (k) => (e) => {
    setFormData((prev) => ({ ...prev, [k]: e.target.value }));
    setDuplicates([]);
  };

  const trimField = (k) => () => {
    setFormData((prev) => ({ ...prev, [k]: (prev[k] || '').trim() }));
  };
  // 06/mai/26: normaliza first_name/last_name (Title Case + preposições BR
  // em minúscula) já no blur — o usuário enxerga o ajuste antes do submit.
  const formatNameField = (k) => () => {
    setFormData((prev) => ({ ...prev, [k]: formatPersonName(prev[k]) }));
  };
  const lowerEmail = () => {
    setFormData((prev) => ({ ...prev, email: (prev.email || '').trim().toLowerCase() }));
  };

  // ── Dedup ────────────────────────────────────────────────────────────────
  const checkDuplicates = useCallback(async () => {
    setCheckingDups(true);
    const found = new Map();
    try {
      if (formData.email && formData.email.trim()) {
        const { data } = await supabase
          .from('contatos')
          .select('id, first_name, last_name, email, mobile, empresa_id, empresas!contatos_empresa_id_fkey(id, company_name, short_name, empresa)')
          .ilike('email', formData.email.trim().toLowerCase())
          .limit(5);
        for (const c of (data || [])) {
          if (!found.has(c.id)) found.set(c.id, { ...c, match_type: 'email' });
        }
      }
      const mobileTail = lastDigits(formData.mobile, 8);
      if (mobileTail.length >= 8) {
        const { data } = await supabase
          .from('contatos')
          .select('id, first_name, last_name, email, mobile, empresa_id, empresas!contatos_empresa_id_fkey(id, company_name, short_name, empresa)')
          .ilike('mobile', `%${mobileTail}%`)
          .limit(5);
        for (const c of (data || [])) {
          if (!found.has(c.id)) found.set(c.id, { ...c, match_type: 'mobile' });
        }
      }
      const arr = Array.from(found.values());
      // Enriquecer com info de employment para a empresa selecionada
      if (arr.length > 0 && empresaSelected?.id) {
        const ids = arr.map((c) => c.id);
        const { data: emps } = await supabase
          .from('contact_employments')
          .select('contact_id, company_id, is_current')
          .in('contact_id', ids)
          .eq('company_id', empresaSelected.id);
        for (const c of arr) {
          const emp = (emps || []).find((e) => e.contact_id === c.id);
          c.has_current_here = emp?.is_current === true;
          c.has_any_here = !!emp;
        }
      }
      setDuplicates(arr);
      return arr;
    } finally {
      setCheckingDups(false);
    }
  }, [formData.email, formData.mobile, empresaSelected?.id]);

  // ── Salvar ──────────────────────────────────────────────────────────────
  const requireMinimal = () => {
    if (!formData.first_name.trim()) return 'Nome é obrigatório';
    if (!empresaSelected?.id) return 'Empresa é obrigatória';
    return null;
  };

  const submitCreate = useCallback(async () => {
    const v = requireMinimal();
    if (v) { setError(v); return; }
    setError(null);
    setSaving(true);
    try {
      const tipoContato = deriveTipoContato(empresaSelected.papel_negocio);
      // 06/mai/26: garante normalização final antes do submit (caso o usuário
      // tenha digitado e mandado sem disparar o blur).
      const fnNorm = formatPersonName(formData.first_name);
      const lnNorm = formatPersonName(formData.last_name);
      const { data, error: err } = await supabase.rpc('rpc_create_contact_with_employment', {
        p_first_name: fnNorm,
        p_last_name: lnNorm || null,
        p_email: formData.email || null,
        p_mobile: formData.mobile || null,
        p_phone: formData.phone || null,
        p_title: formData.title || null,
        p_empresa_id: empresaSelected.id,
        p_tipo_contato: tipoContato,
        p_contact_nature: 'person',
        p_source: source,
      });
      if (err) throw err;
      onSuccess?.({
        ...(data || {}),
        linked_existing: false,
        empresa: empresaSelected,
        contact: { ...formData },
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, empresaSelected, source, onSuccess]);

  const submitLink = useCallback(async (existing) => {
    if (!empresaSelected?.id) { setError('Empresa é obrigatória'); return; }
    setError(null);
    setSaving(true);
    try {
      const { data, error: err } = await supabase.rpc('rpc_link_existing_contact_to_company', {
        p_contact_id: existing.id,
        p_empresa_id: empresaSelected.id,
        p_title: formData.title || null,
        p_source: source,
      });
      if (err) throw err;
      onSuccess?.({
        ...(data || {}),
        linked_existing: true,
        empresa: empresaSelected,
        contact: existing,
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaSelected, formData.title, source, onSuccess]);

  const handlePrimary = async () => {
    const v = requireMinimal();
    if (v) { setError(v); return; }
    setError(null);
    const dups = await checkDuplicates();
    if (dups.length === 0) {
      submitCreate();
    }
    // Se há duplicatas, o usuário escolhe ação no painel de aviso.
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[11px] font-semibold text-slate-600 mb-1">Nome *</span>
          <input
            type="text"
            value={formData.first_name}
            onChange={setField('first_name')}
            onBlur={formatNameField('first_name')}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="block text-[11px] font-semibold text-slate-600 mb-1">Sobrenome</span>
          <input
            type="text"
            value={formData.last_name}
            onChange={setField('last_name')}
            onBlur={formatNameField('last_name')}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[11px] font-semibold text-slate-600 mb-1">Celular</span>
          <input
            type="tel"
            value={formData.mobile}
            onChange={setField('mobile')}
            onBlur={trimField('mobile')}
            placeholder="+55 11 9..."
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-[11px] font-semibold text-slate-600 mb-1">Telefone</span>
          <input
            type="tel"
            value={formData.phone}
            onChange={setField('phone')}
            onBlur={trimField('phone')}
            placeholder="+55 11 3..."
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-[11px] font-semibold text-slate-600 mb-1">Email</span>
        <input
          type="email"
          value={formData.email}
          onChange={setField('email')}
          onBlur={lowerEmail}
          placeholder="email@..."
          className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
        />
      </label>

      <label className="block">
        <span className="block text-[11px] font-semibold text-slate-600 mb-1">Cargo</span>
        <input
          type="text"
          value={formData.title}
          onChange={setField('title')}
          onBlur={trimField('title')}
          className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
        />
      </label>

      <div className="block">
        <span className="block text-[11px] font-semibold text-slate-600 mb-1">Empresa *</span>
        {empresaSelected ? (
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
            <div className="text-sm font-medium text-emerald-900 truncate" title={companyDisplay}>
              ✓ {companyDisplay}
            </div>
            <button
              type="button"
              onClick={clearEmpresa}
              className="text-emerald-700 hover:text-emerald-900 text-xs flex items-center gap-1"
              title="Trocar empresa"
            >
              <X size={12} /> trocar
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="flex items-center gap-1.5 border border-slate-300 rounded px-2 py-1.5">
              <Search size={12} className="text-slate-400" />
              <input
                type="text"
                value={empresaQuery}
                onChange={(e) => setEmpresaQuery(e.target.value)}
                placeholder="Digite ≥2 letras (CONX, CYRELA, BTG...)"
                className="flex-1 outline-none text-sm"
                autoComplete="off"
              />
              {searchingEmpresa && <Loader2 size={12} className="animate-spin text-slate-400" />}
            </div>
            {empresaResults.length > 0 && (
              <ul className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded shadow max-h-56 overflow-y-auto">
                {empresaResults.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => pickEmpresa(e)}
                      className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-sm"
                    >
                      <div className="font-medium text-slate-800">{getCompanyDisplayName(e)}</div>
                      {e.company_name !== getCompanyDisplayName(e) && (
                        <div className="text-[10px] text-slate-500 truncate">{e.company_name}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {duplicates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[12px] text-amber-900 space-y-1.5">
          <div className="flex items-center gap-1 font-semibold">
            <AlertTriangle size={12} /> Possível contato já existente
          </div>
          <ul className="space-y-1">
            {duplicates.map((c) => {
              const cName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(sem nome)';
              const cEmp = c.empresas ? getCompanyDisplayName(c.empresas) : '—';
              return (
                <li key={c.id} className="bg-white border border-amber-200 rounded p-2 flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{cName}</span>
                    {c.email && <span className="text-slate-500 text-[11px]">{c.email}</span>}
                    {cEmp && <span className="text-slate-500 text-[11px]">· {cEmp}</span>}
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-700">
                      match: {c.match_type}
                    </span>
                  </div>
                  {c.has_current_here ? (
                    <div className="text-[11px] text-red-700">
                      Já tem vínculo ativo com a empresa selecionada — provavelmente é o mesmo contato.
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => submitLink(c)}
                        disabled={saving}
                        className="px-2 py-1 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Vincular este contato à empresa
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={submitCreate}
              disabled={saving}
              className="px-2 py-1 text-[11px] bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              Criar novo mesmo assim
            </button>
            <button
              type="button"
              onClick={() => setDuplicates([])}
              disabled={saving}
              className="px-2 py-1 text-[11px] bg-white border border-amber-300 text-amber-800 rounded hover:bg-amber-100 disabled:opacity-50"
            >
              Voltar e revisar
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-[12px] text-red-800">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-[12px] bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handlePrimary}
          disabled={saving || checkingDups}
          className="px-3 py-1.5 text-[12px] bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
        >
          {(saving || checkingDups) && <Loader2 size={12} className="animate-spin" />}
          {saving ? 'Salvando…' : checkingDups ? 'Verificando…' : 'Salvar contato'}
        </button>
      </div>
      <div className="text-[10px] text-slate-400 text-right">
        source: {source} · usuário: {user?.email || '—'}
      </div>
    </div>
  );
};

export default ContactCreateForm;
